// Split out of CompactChatInput so the layout math is unit-testable (jsdom
// reports zero widths).

/** Matches the textarea's `lineHeight`; used to count wrapped lines. */
export const TEXTAREA_LINE_HEIGHT_PX = 20;

/** Horizontal gap (theme `gap={2}`) between the inline controls and the text. */
const INLINE_CONTROL_GAP_PX = 8;

export const MIN_INLINE_TEXT_WIDTH_PX = 200;

/** Concentric corners: box radius = wrapper radius - wrapper padding. */
export const ELEVATED_WRAPPER_RADIUS_PX = 32;
export const ELEVATED_WRAPPER_PADDING_PX = 4;
export const COMPOSER_BOX_RADIUS_PX =
  ELEVATED_WRAPPER_RADIUS_PX - ELEVATED_WRAPPER_PADDING_PX;

/** The text's inset inside the composer box. */
export const COMPOSER_TEXT_PADDING_PX = 20;

/** Where the composer's text starts, from the ring wrapper's outer edge, so content above the composer can line up with it. */
export const COMPOSER_TEXT_INSET_PX =
  ELEVATED_WRAPPER_PADDING_PX + COMPOSER_TEXT_PADDING_PX;

/** Lets the ring wrapper's `:hover` light the box too (see LIT_BOX). */
export const COMPOSER_BOX_ATTR = "data-composer-box";
export const COMPOSER_BOX_SELECTOR = `& [${COMPOSER_BOX_ATTR}]`;

/** Marks the ring wrapper itself, so tests can target the band. */
export const COMPOSER_RING_ATTR = "data-composer-ring";

/** Exposes the resolved layout so tests can observe the measured decision. */
export const COMPOSER_LAYOUT_ATTR = "data-composer-layout";

/** Marks a toolbar scroller so layout measurements can find its hidden overflow. */
export const COMPOSER_TOOLBAR_SCROLLER_ATTR = "data-composer-toolbar-scroller";

/**
 * An element's width as if no toolbar scroller inside it were clipping its
 * controls: what the controls need, not what they currently get.
 */
export const getUnclippedWidth = (element: HTMLElement | null): number => {
  if (!element) return 0;
  const scroller = element.querySelector<HTMLElement>(
    `[${COMPOSER_TOOLBAR_SCROLLER_ATTR}]`,
  );
  const hiddenOverflow = scroller
    ? scroller.scrollWidth - scroller.clientWidth
    : 0;
  return element.offsetWidth + hiddenOverflow;
};

/** The input box's hovered/lit treatment, applied from the ring wrapper. */
export const LIT_BOX = {
  backgroundColor: "white",
} as const;

export const getSingleLineTextWidth = ({
  contentWidth,
  leftControlsWidth,
  rightControlsWidth,
}: {
  contentWidth: number;
  leftControlsWidth: number;
  rightControlsWidth: number;
}): number =>
  contentWidth -
  leftControlsWidth -
  rightControlsWidth -
  INLINE_CONTROL_GAP_PX * 2;
